import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { getDashboardStats } from "@/lib/api/contacts.functions";
import { getDashboardAnalytics, getVisitorLog } from "@/lib/api/analytics.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { VisitorTrendChart } from "@/components/admin/analytics/VisitorTrendChart";
import { VisitorLogTable } from "@/components/admin/analytics/VisitorLogTable";

const VisitorMap = lazy(() =>
  import("@/components/admin/analytics/VisitorMap").then((m) => ({ default: m.VisitorMap })),
);

export const Route = createFileRoute("/admin/")({
  loader: async () => {
    const [stats, analytics, visitors] = await Promise.all([
      getDashboardStats(),
      getDashboardAnalytics(),
      getVisitorLog({ data: { limit: 100 } }),
    ]);
    return { stats, analytics, visitors };
  },
  component: AdminDashboard,
});

function AdminDashboard() {
  const { stats, analytics, visitors } = Route.useLoaderData();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Overview of site content, enquiries, and visitor analytics</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">New enquiries</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.newEnquiries}</p>
            <Link to="/admin/contacts" className="text-sm text-primary hover:underline">View inbox →</Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Unique visitors today</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{analytics.todayUniqueVisitors}</p>
            <p className="text-sm text-muted-foreground">Based on IP (daily dedupe)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Draft insights</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.draftInsights}</p>
            <Link to="/admin/insights" className="text-sm text-primary hover:underline">Manage insights →</Link>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Published insights</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.publishedInsights}</p>
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <VisitorTrendChart data={analytics.dailySeries} />
        <Suspense
          fallback={
            <Card>
              <CardHeader>
                <CardTitle>Visitor locations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
                  Loading map…
                </div>
              </CardContent>
            </Card>
          }
        >
          <VisitorMap locations={analytics.mapLocations} />
        </Suspense>
      </div>
      <VisitorLogTable rows={visitors.rows} />
    </div>
  );
}
